import type { CommandServices } from '@wix/cli-command-contract';
import { Command } from '@commander-js/extra-typings';
import { account } from '@wix/cli-account-commands';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { isType, match } from 'variant';
import packageJson from '../package.json' with { type: 'json' };
import { login } from './commands/login.js';
import { logout } from './commands/logout.js';
import { telemetry } from './commands/telemetry.js';
import { token } from './commands/token.js';
import { whoami } from './commands/whoami.js';
import { helpConfiguration } from './help.js';
import { identifyProgramFlow } from './program-flow.js';

interface ProgramOptions
  extends Pick<
    CommandServices,
    'biLogger' | 'errorReporter' | 'loginMode' | 'panorama'
  > {
  projectFolder: string;
}

export async function createProgram({
  biLogger,
  errorReporter,
  loginMode,
  panorama,
  projectFolder,
}: ProgramOptions) {
  const program = new Command();

  program.name('wix');
  program.version(
    packageJson.version,
    '-v, --version',
    'Output the version number'
  );

  // By default commander doesn't care about positional options
  // so if we had a `--bar` in `wix --bar` root command, or in a `wix foo --bar` sub-command
  // commander would treat them both the same.
  program.enablePositionalOptions(true);
  program.helpOption('-h, --help', 'Display help for command');
  program.helpCommand(false);
  program.configureHelp(helpConfiguration);

  const flow = await identifyProgramFlow(projectFolder);

  let siteId: string | undefined;

  await match(flow, {
    APP: async ({ getRootCommand }) => {
      const app = await getRootCommand(
        { biLogger, errorReporter, loginMode, panorama },
        packageJson.version
      );
      program.addCommand(app);
    },
    ASTRO: async ({ definedWixConfig, getRootCommand }) => {
      const astro = await getRootCommand(
        { biLogger, definedWixConfig, errorReporter, loginMode, panorama },
        packageJson.version
      );
      siteId = (definedWixConfig.config as { siteId?: string }).siteId;
      for (const command of astro.commands) {
        program.addCommand(command);
      }
    },
    SITE: async ({ getRootCommand }) => {
      const site = await getRootCommand(
        { biLogger, errorReporter, loginMode, panorama },
        packageJson.version
      );
      for (const command of site.commands) {
        program.addCommand(command);
      }
    },
    UNKNOWN: ({ reason }) => {
      program.on('command:*', () => {
        throw new CliError({
          cause: undefined,
          code: CliErrorCode.FailedToIdentifyProgramFlow({ reason }),
        });
      });
    },
  });

  program
    .addCommand(login({ biLogger, errorReporter, loginMode, panorama }))
    .addCommand(whoami({ biLogger, errorReporter, loginMode, panorama }))
    .addCommand(token({ biLogger, errorReporter, loginMode, panorama }))
    .addCommand(
      account(
        { biLogger, errorReporter, loginMode, panorama },
        packageJson.version,
        siteId
      )
    )
    .addCommand(logout({ biLogger, errorReporter, loginMode, panorama }))
    .addCommand(telemetry());

  const hasCliAppInstalled = isType(flow, 'APP');
  return { hasCliAppInstalled, program };
}
